import { Reveal } from "@/components/Reveal";
import { motion } from "framer-motion";
import { Trophy, Flag, MapTrifold, Airplane, Compass, Fire } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

const STAT_CONFIG = [
    { icon: Flag, labelKey: "passportShowcase.stats.countries", defaultLabel: "Countries visited", value: "42" },
    { icon: MapTrifold, labelKey: "passportShowcase.stats.cities", defaultLabel: "Cities stamped", value: "138" },
    { icon: Airplane, labelKey: "passportShowcase.stats.miles", defaultLabel: "Miles traveled", value: "82,410" },
    { icon: Trophy, labelKey: "passportShowcase.stats.level", defaultLabel: "Level earned", value: "Lvl 12" },
    { icon: Compass, labelKey: "passportShowcase.stats.bucket", defaultLabel: "Bucket list", value: "18 / 40" },
    { icon: Fire, labelKey: "passportShowcase.stats.explored", defaultLabel: "World explored", value: "21.5%" },
];

const STAMPS = [
    { c: "Cambodia", city: "Siem Reap", img: "https://images.pexels.com/photos/32609063/pexels-photo-32609063.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400" },
    { c: "Greece", city: "Oia", img: "https://images.pexels.com/photos/20197709/pexels-photo-20197709.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400" },
    { c: "Jordan", city: "Wadi Musa", img: "https://images.unsplash.com/photo-1580060839134-75a5edca2e99?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxjYXBlJTIwdG93bnxlbnwwfHx8fDE3ODQ2NzM0MDN8MA&ixlib=rb-4.1.0&q=85" },
    { c: "Japan", city: "Kyoto", img: "https://images.pexels.com/photos/35072449/pexels-photo-35072449.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400" },
    { c: "Portugal", city: "Alfama", img: "https://images.pexels.com/photos/13114680/pexels-photo-13114680.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400" },
    { c: "Indonesia", city: "Ubud", img: "https://images.pexels.com/photos/8085317/pexels-photo-8085317.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400" },
];

export default function PassportShowcase() {
    const { t } = useTranslation();
    return (
        <section id="atlas" className="relative py-32 md:py-40" data-testid="passport-showcase">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <Reveal>
                    <div className="grid md:grid-cols-2 gap-8 items-end mb-16">
                        <div>
                            <div className="overline mb-4">{t("passportShowcase.overline")}</div>
                            <h2 className="font-serif text-5xl md:text-7xl text-white leading-[0.95] tracking-tighter">
                                {t("passportShowcase.titleLine1")}<br />
                                <span style={{ fontStyle: "italic", color: "var(--gold-pale)" }}>{t("passportShowcase.titleLine2")}</span>.
                            </h2>
                        </div>
                        <p className="text-white/60 text-lg leading-relaxed md:pb-3">
                            {t("passportShowcase.subtitle")}
                        </p>
                    </div>
                </Reveal>

                {/* stats row */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px rounded-3xl overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                    {STAT_CONFIG.map((s, i) => {
                        const Icon = s.icon;
                        const label = t(s.labelKey);
                        return (
                            <Reveal key={i} delay={0.03 * i}>
                                <div className="p-6 md:p-8 h-full" style={{ background: "var(--navy)" }} data-testid={`stat-${s.defaultLabel.replace(/\s/g, '-').toLowerCase()}`}>
                                    <Icon size={22} weight="regular" color="#D4AF37" />
                                    <div className="font-serif text-3xl md:text-4xl text-white mt-6 leading-none">{s.value}</div>
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 mt-3">{label}</div>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>

                {/* Stamps row */}
                <Reveal delay={0.2}>
                    <div className="mt-20 flex items-center gap-6 mb-6">
                        <div className="overline">{t("passportShowcase.recentStamps")}</div>
                        <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.08)" }} />
                    </div>
                </Reveal>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {STAMPS.map((s, i) => (
                        <Reveal key={s.c} delay={0.03 * i}>
                            <motion.div
                                whileHover={{ y: -4 }}
                                transition={{ duration: 0.4 }}
                                className="group relative aspect-square rounded-2xl overflow-hidden card-hover"
                                style={{ border: "1px solid rgba(212,175,55,0.15)" }}
                                data-testid={`stamp-${s.c.toLowerCase()}`}
                            >
                                <img src={s.img} alt={s.city} className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-700" />
                                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(11,19,32,0.9))" }} />
                                <div className="absolute inset-x-0 bottom-0 p-4">
                                    <div className="w-8 h-8 rounded-full grid place-items-center mb-2" style={{ background: "rgba(212,175,55,0.15)", border: "1px solid var(--gold)" }}>
                                        <Airplane size={12} color="#D4AF37" />
                                    </div>
                                    <div className="text-xs text-white/60">{s.c}</div>
                                    <div className="font-serif text-lg text-white leading-tight">{s.city}</div>
                                </div>
                            </motion.div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
